import type { useViewPdfDataQuery$variables } from './hooks/__generated__/useViewPdfDataQuery.graphql.ts';

export const DEFAULT_VIEWER_WIDTH = '100%';
export const DEFAULT_VIEWER_HEIGHT = '650px';

export type ViewPdfMacroParams = {
	''?: { value?: string };
	defaultParameterValue?: { value?: string };
	name?: { value?: string };
	page?: { value?: string };
	date?: { value?: string };
	space?: { value?: string };
	width?: { value?: string };
	height?: { value?: string };
};

export type DecodedPageParams = {
	pageName: string | null;
	spaceKey: string | null;
	date: string | null;
};

export type ParsedViewPdfMacroParams = {
	attachmentName: string | null;
	pageName: string | null;
	spaceKey: string | null;
	date: string | null;
	viewerWidth: string;
	viewerHeight: string;
};

export type BuildViewPdfQueryVariablesOptions = {
	contentId: string | null | undefined;
	pageName?: string | null;
	spaceKey?: string | null;
	date?: string | null;
};

/**
 * Normalises a size string from macro params.
 * Matches the legacy ConverterMacro.convertToSizeParam() behaviour:
 *   - Ends with "%"  -> strip non-numeric chars except decimal points, append "%"
 *   - Otherwise      -> strip non-numeric chars except decimal points, append "px"
 */
export const normaliseSizeParam = (size: string): string => {
	const numericSize = size.replace(/[^\d.]+/g, '');

	if (size.endsWith('%')) {
		return `${numericSize}%`;
	}

	return `${numericSize}px`;
};

export const computeViewerDimensions = (
	rawWidth: string | undefined,
	rawHeight: string | undefined,
): { width: string; height: string } => ({
	width: rawWidth ? normaliseSizeParam(rawWidth) : DEFAULT_VIEWER_WIDTH,
	height: rawHeight ? normaliseSizeParam(rawHeight) : DEFAULT_VIEWER_HEIGHT,
});

/**
 * Compatibility parser for legacy Office Connector page param encodings
 * like "SPACEKEY:Page Title" and "SPACEKEY:/yyyy/mm/dd/Blog Title".
 */
export const decodeLegacyMacroPageParam = (rawPageParam: string | null): DecodedPageParams => {
	if (!rawPageParam) {
		return { pageName: null, spaceKey: null, date: null };
	}

	const pageParam = rawPageParam.trim();
	if (!pageParam) {
		return { pageName: null, spaceKey: null, date: null };
	}

	const firstColon = pageParam.indexOf(':');
	const spacePart = firstColon !== -1 ? pageParam.slice(0, firstColon) || null : null;
	const titlePart = firstColon !== -1 ? pageParam.slice(firstColon + 1) : pageParam;

	let pageName = titlePart;
	let date: string | null = null;

	const blogMatch = titlePart.match(/^\/(\d{4})\/(\d{2})\/(\d{2})\/(.+)$/);
	if (blogMatch) {
		const [, year, month, day, title] = blogMatch;
		date = `${month}/${day}/${year}`;
		pageName = title;
	}

	return {
		pageName: pageName || null,
		spaceKey: spacePart || null,
		date,
	};
};

export const parseViewPdfMacroParams = (
	macroParams?: ViewPdfMacroParams,
): ParsedViewPdfMacroParams => {
	const attachmentName =
		macroParams?.name?.value ??
		macroParams?.['']?.value ??
		macroParams?.defaultParameterValue?.value ??
		null;
	const decodedPage = decodeLegacyMacroPageParam(macroParams?.page?.value ?? null);
	const { width: viewerWidth, height: viewerHeight } = computeViewerDimensions(
		macroParams?.width?.value,
		macroParams?.height?.value,
	);

	return {
		attachmentName,
		pageName: decodedPage.pageName,
		spaceKey: macroParams?.space?.value ?? decodedPage.spaceKey,
		date: macroParams?.date?.value ?? decodedPage.date,
		viewerWidth,
		viewerHeight,
	};
};

/**
 * Converts a blog post date string from mm/dd/yyyy to yyyy-mm-dd format
 * as expected by the AGG `postingDay` argument.
 */
export const formatPostingDay = (date: string): string | undefined => {
	const parts = date.split('/');
	if (parts.length !== 3) {
		return undefined;
	}
	const [month, day, year] = parts;
	return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

/**
 * Builds variables for useViewPdfDataQuery.
 *
 * Same-page mode: query by contentId.
 * Cross-page mode: query by page title (+ optional spaceKey/date).
 */
export const buildViewPdfQueryVariables = ({
	contentId,
	pageName,
	spaceKey,
	date,
}: BuildViewPdfQueryVariablesOptions): useViewPdfDataQuery$variables => {
	const normalizedPageName = pageName?.trim() || null;
	if (normalizedPageName) {
		const variables: useViewPdfDataQuery$variables = {
			contentStatus: ['current'],
			title: normalizedPageName,
			type: date ? 'blogpost' : 'page',
		};

		if (spaceKey) {
			variables.spaceKey = spaceKey;
		}

		if (date) {
			variables.postingDay = formatPostingDay(date);
		}

		return variables;
	}

	return {
		contentId: contentId ?? undefined,
		contentStatus: ['current'],
	};
};
